import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import TranslationService, { LanguageStorage, SupportedLanguage } from '../services/translationService';

interface Language {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
}

// Only two languages supported: English and Chinese
const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' }
];

interface LanguageContextType {
  // Current UI language
  uiLanguage: SupportedLanguage;
  
  // Current content language (for AI generation)
  contentLanguage: SupportedLanguage;
  
  // Loading state
  loading: boolean;
  
  // Change UI language
  setUILanguage: (lang: SupportedLanguage) => Promise<void>;
  
  // Change content language
  setContentLanguage: (lang: SupportedLanguage) => void;
  
  // Get translated string for UI
  t: (key: string, params?: Record<string, any>) => string;
  
  // Get available languages
  getSupportedLanguages: () => Language[];
  
  // Check if translation exists
  hasTranslation: (key: string, lang?: SupportedLanguage) => boolean;
}

// Create the language context
const LanguageContext = createContext<LanguageContextType>({} as LanguageContextType);

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Provider component to wrap the app
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uiLanguage, setUILanguageState] = useState<SupportedLanguage>('en');
  const [contentLanguage, setContentLanguageState] = useState<SupportedLanguage>('en');
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize language preferences from localStorage on mount
  useEffect(() => {
    const initializeLanguages = async () => {
      try {
        setLoading(true);
        
        // Load language preferences from localStorage (defaults to 'en')
        const storedUILanguage = LanguageStorage.getUILanguage();
        const storedContentLanguage = LanguageStorage.getContentLanguage();
        
        setUILanguageState(storedUILanguage);
        setContentLanguageState(storedContentLanguage);
        
        // Load translations for the UI language
        await TranslationService.loadTranslations(storedUILanguage);
        
        // Preload the other language in the background
        const otherLanguage: SupportedLanguage = storedUILanguage === 'en' ? 'zh' : 'en';
        setTimeout(() => {
          TranslationService.preloadLanguage(otherLanguage);
        }, 1000);
        
      } catch (error) {
        console.error('Error initializing languages:', error);
        // Fallback to English on error
        setUILanguageState('en');
        setContentLanguageState('en');
      } finally {
        setLoading(false);
      }
    };

    initializeLanguages();
  }, []);

  // Change UI language
  const setUILanguage = useCallback(async (lang: SupportedLanguage) => {
    try {
      // Clear cache to force fresh load
      TranslationService.clearCache();
      
      // Load translations for the new language
      await TranslationService.loadTranslations(lang);
      
      // Update state
      setUILanguageState(lang);
      
      // Persist to localStorage
      LanguageStorage.setUILanguage(lang);
      
    } catch (error) {
      console.error('Error changing UI language:', error);
      throw error;
    }
  }, []);

  // Change content language
  const setContentLanguage = useCallback((lang: SupportedLanguage) => {
    // Update state
    setContentLanguageState(lang);
    
    // Persist to localStorage
    LanguageStorage.setContentLanguage(lang);
  }, []);

  // Translation function
  const t = useCallback((key: string, params?: Record<string, any>): string => {
    return TranslationService.translate(key, uiLanguage, params);
  }, [uiLanguage]);

  // Get supported languages
  const getSupportedLanguages = useCallback((): Language[] => {
    return SUPPORTED_LANGUAGES;
  }, []);

  // Check if translation exists
  const hasTranslation = useCallback((key: string, lang?: SupportedLanguage): boolean => {
    const languageToCheck = lang || uiLanguage;
    return TranslationService.hasTranslation(key, languageToCheck);
  }, [uiLanguage]);

  // Context value
  const value: LanguageContextType = {
    uiLanguage,
    contentLanguage,
    loading,
    setUILanguage,
    setContentLanguage,
    t,
    getSupportedLanguages,
    hasTranslation
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageProvider;
export { SUPPORTED_LANGUAGES };
export type { Language, LanguageContextType };
